import { DecimalPipe, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormRecord,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { BlockSpinnerDirective, ExtendedNotificationComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import {
  CreateDairyYieldScanRequest,
  DairyYieldScanAnswerOption,
  DairyYieldScanQuestionCategory,
  DairyYieldScanQuestionnaire,
} from '../../models/dairy-yield-scan.models';
import {
  DairyYieldCalculationInput,
  DairyYieldCalculationService,
} from '../../services/dairy-yield-calculation.service';
import { DairyYieldScanService } from '../../services/dairy-yield-scan.service';
import { DairyYieldScanCoverComponent } from '../dairy-yield-scan-cover/dairy-yield-scan-cover.component';

const ANSWER_OPTIONS: ReadonlyArray<{
  value: DairyYieldScanAnswerOption;
  labelKey: string;
  fallbackImage: string;
}> = [
  {
    value: 'Improvement',
    labelKey: 'dairyYieldScan.answerOptions.improvement',
    fallbackImage: 'assets/images/verbetering-mogelijk.png',
  },
  {
    value: 'Sufficient',
    labelKey: 'dairyYieldScan.answerOptions.sufficient',
    fallbackImage: 'assets/images/voldoende.png',
  },
  {
    value: 'Optimal',
    labelKey: 'dairyYieldScan.answerOptions.optimal',
    fallbackImage: 'assets/images/optimaal.png',
  },
];
const PROGRESS_ACTIVATION_OFFSET_PX = 160;
const MAX_PERCENTAGE = 100;
const PRODUCTION_INPUT_CONTROL_NAMES = [
  'currentMilkProduction',
  'currentFatPercentage',
  'currentProteinPercentage',
  'targetMilkProduction',
  'targetFatPercentage',
  'targetProteinPercentage',
] as const;
type ProductionInputControlName = (typeof PRODUCTION_INPUT_CONTROL_NAMES)[number];

const roundToDecimals = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const questionnaireAnswerImagePath = (
  questionKey: string,
  answerOption: DairyYieldScanAnswerOption,
): string => {
  const questionName = questionKey.split('.').at(-1);
  return `assets/images/questionnaire/${questionName}.${answerOption.toLowerCase()}.webp`;
};

@Component({
  selector: 'app-dairy-yield-scan',
  templateUrl: './dairy-yield-scan.component.html',
  styleUrls: ['./dairy-yield-scan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgrNumberDirective,
    BlockSpinnerDirective,
    DecimalPipe,
    DairyYieldScanCoverComponent,
    ExtendedNotificationComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class DairyYieldScanComponent implements OnInit {
  readonly answerOptions = ANSWER_OPTIONS;
  readonly answerImagePath = questionnaireAnswerImagePath;
  readonly errorNotificationType = 'error';
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  readonly submitting = signal(false);
  readonly submissionAttempted = signal(false);
  readonly questionnaire = signal<DairyYieldScanQuestionnaire | null>(null);
  readonly activeProgressStep = signal(0);
  readonly started = signal(false);

  readonly contextForm = new FormGroup({
    farmerName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(200)],
    }),
    customerNumber: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(50)],
    }),
    dairyCowCount: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(2147483647),
      Validators.pattern(/^\d+$/),
    ]),
    currentMilkProduction: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(999999.99),
    ]),
    currentFatPercentage: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(MAX_PERCENTAGE),
    ]),
    currentProteinPercentage: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(MAX_PERCENTAGE),
    ]),
    targetMilkProduction: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(999999.99),
    ]),
    targetFatPercentage: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(MAX_PERCENTAGE),
    ]),
    targetProteinPercentage: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(MAX_PERCENTAGE),
    ]),
  });

  readonly currentMilkYield = computed(() => {
    this.contextValues();
    return this.calculateMilkYield(
      'currentMilkProduction',
      'currentFatPercentage',
      'currentProteinPercentage',
    );
  });
  readonly currentFatAndProteinYield = computed(() => {
    this.contextValues();
    return this.calculateFatAndProteinYield(
      'currentMilkProduction',
      'currentFatPercentage',
      'currentProteinPercentage',
    );
  });
  readonly targetMilkYield = computed(() => {
    this.contextValues();
    return this.calculateMilkYield(
      'targetMilkProduction',
      'targetFatPercentage',
      'targetProteinPercentage',
    );
  });
  readonly targetFatAndProteinYield = computed(() => {
    this.contextValues();
    return this.calculateFatAndProteinYield(
      'targetMilkProduction',
      'targetFatPercentage',
      'targetProteinPercentage',
    );
  });

  readonly answerControls = new FormRecord<FormControl<DairyYieldScanAnswerOption | null>>({});
  readonly totalQuestions = computed(
    () =>
      this.questionnaire()?.categories.reduce(
        (total, category) => total + category.questions.length,
        0,
      ) ?? 0,
  );
  readonly progress = computed(() => {
    const categories = this.questionnaire()?.categories ?? [];
    if (categories.length === 0) {
      return 0;
    }

    const completedSegments = categories.reduce(
      (total, category) => total + this.answeredInCategory(category) / category.questions.length,
      0,
    );

    return Math.round((completedSegments / categories.length) * 100);
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly answerValues = toSignal(this.answerControls.valueChanges, {
    initialValue: this.answerControls.getRawValue(),
  });
  private readonly contextValues = toSignal(this.contextForm.valueChanges, {
    initialValue: this.contextForm.getRawValue(),
  });

  constructor(
    private readonly dairyYieldCalculationService: DairyYieldCalculationService,
    private readonly dairyYieldScanService: DairyYieldScanService,
    private readonly dialogService: DialogService,
    private readonly locale: LocaleService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}

  @HostListener('window:resize')
  updateActiveProgressStep() {
    const sectionIds = this.progressSectionIds();
    let activeStep = 0;

    for (let index = 0; index < sectionIds.length; index++) {
      const section = this.document.getElementById(sectionIds[index]);
      if (section && section.getBoundingClientRect().top <= PROGRESS_ACTIVATION_OFFSET_PX) {
        activeStep = index;
      }
    }

    this.activeProgressStep.set(activeStep);
  }

  ngOnInit() {
    this.document.addEventListener('scroll', this.onDocumentScroll, true);
    this.destroyRef.onDestroy(() =>
      this.document.removeEventListener('scroll', this.onDocumentScroll, true),
    );
    this.loadQuestionnaire();
  }

  loadQuestionnaire() {
    this.loading.set(true);
    this.loadFailed.set(false);

    this.dairyYieldScanService
      .getQuestionnaire()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (questionnaire) => {
          for (const question of questionnaire.categories.flatMap(
            (category) => category.questions,
          )) {
            if (!this.answerControls.contains(question.question)) {
              this.answerControls.addControl(
                question.question,
                new FormControl<DairyYieldScanAnswerOption | null>(null, Validators.required),
              );
            }
          }
          this.questionnaire.set(questionnaire);
        },
        error: () => this.loadFailed.set(true),
      });
  }

  answerControl(questionKey: string): FormControl<DairyYieldScanAnswerOption | null> {
    return this.answerControls.controls[questionKey];
  }

  answeredInCategory(category: DairyYieldScanQuestionCategory): number {
    const answers = this.answerValues();
    return category.questions.filter(
      ({ question }) => answers[question] !== null && answers[question] !== undefined,
    ).length;
  }

  isUnanswered(questionKey: string): boolean {
    return this.submissionAttempted() && this.answerControl(questionKey).invalid;
  }

  hasExplanation(questionKey: string): boolean {
    const key = `${questionKey}.explanation`;
    const translation = this.locale.translate(key);
    return translation !== key && translation.trim().length > 0;
  }

  openExplanation(questionKey: string) {
    if (!this.hasExplanation(questionKey)) {
      return;
    }

    this.dialogService.notify(
      {
        title: this.locale.translate(`${questionKey}.title`),
        text: this.locale.translate(`${questionKey}.explanation`),
        cancelButtonText: this.locale.translate('dairyYieldScan.explanation.close'),
      },
      { width: 'calc(100% - 32px)', maxWidth: '720px' },
    );
  }

  openMeetmelkExplanation() {
    this.dialogService.notify(
      {
        title: this.locale.translate('dairyYieldScan.meetmelkExplanation.title'),
        text: this.locale.translate('dairyYieldScan.meetmelkExplanation.text'),
        cancelButtonText: this.locale.translate('dairyYieldScan.explanation.close'),
      },
      { width: 'calc(100% - 32px)', maxWidth: '720px' },
    );
  }

  useFallbackAnswerImage(event: Event, fallbackImage: string) {
    const image = event.target as HTMLImageElement;
    if (image.getAttribute('src') !== fallbackImage) {
      image.setAttribute('src', fallbackImage);
    }
  }

  scrollToSection(sectionId: string) {
    this.document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  startScan() {
    this.started.set(true);
    this.document.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  roundProductionInput(controlName: ProductionInputControlName) {
    const control = this.contextForm.controls[controlName];
    if (control.value !== null) {
      control.setValue(
        roundToDecimals(control.value, this.productionInputDecimalPlaces(controlName)),
      );
    }
  }

  submit(event?: SubmitEvent) {
    event?.preventDefault();

    this.submissionAttempted.set(true);
    this.contextForm.markAllAsTouched();
    this.answerControls.markAllAsTouched();

    if (this.contextForm.invalid || this.answerControls.invalid || this.submitting()) {
      const invalidContextControl = Object.entries(this.contextForm.controls).find(
        ([, control]) => control.invalid,
      );
      if (invalidContextControl) {
        this.scrollToSection('scan-details');
        queueMicrotask(() =>
          this.document.getElementById(`context-${invalidContextControl[0]}`)?.focus(),
        );
        return;
      }

      const firstUnanswered = this.questionnaire()
        ?.categories.flatMap((category) => category.questions)
        .find(({ question }) => this.answerControl(question).invalid);
      if (firstUnanswered) {
        const questionElementId = `question-${firstUnanswered.question}`;
        this.scrollToSection(questionElementId);
        queueMicrotask(() =>
          this.document.getElementById(questionElementId)?.querySelector('input')?.focus(),
        );
      }
      return;
    }

    this.submitting.set(true);
    this.dairyYieldScanService
      .createScan(this.createRequest())
      .pipe(
        finalize(() => this.submitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ id }) => {
          void this.router.navigate(['/scans', id, 'summary']);
        },
        error: () => {
          this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: this.locale.translate('dairyYieldScan.submit.error'),
            }),
          );
        },
      });
  }

  private readonly onDocumentScroll = () => this.updateActiveProgressStep();

  private createRequest(): CreateDairyYieldScanRequest {
    for (const controlName of PRODUCTION_INPUT_CONTROL_NAMES) {
      this.roundProductionInput(controlName);
    }
    const context = this.contextForm.getRawValue();
    const questionnaire = this.questionnaire();

    return {
      farmerName: context.farmerName.trim() || null,
      customerNumber: context.customerNumber.trim() || null,
      dairyCowCount: context.dairyCowCount,
      currentMilkProduction: context.currentMilkProduction,
      currentFatPercentage: context.currentFatPercentage,
      currentProteinPercentage: context.currentProteinPercentage,
      targetMilkProduction: context.targetMilkProduction,
      targetFatPercentage: context.targetFatPercentage,
      targetProteinPercentage: context.targetProteinPercentage,
      answers:
        questionnaire?.categories.flatMap((category) =>
          category.questions.map(({ question }) => ({
            questionKey: question,
            option: this.answerControl(question).value as DairyYieldScanAnswerOption,
          })),
        ) ?? [],
    };
  }

  private calculateMilkYield(
    milkControlName: 'currentMilkProduction' | 'targetMilkProduction',
    fatControlName: 'currentFatPercentage' | 'targetFatPercentage',
    proteinControlName: 'currentProteinPercentage' | 'targetProteinPercentage',
  ): number | null {
    const values = this.productionValues(milkControlName, fatControlName, proteinControlName);
    if (!values) {
      return null;
    }

    return this.dairyYieldCalculationService.calculateMeetmelk(values);
  }

  private calculateFatAndProteinYield(
    milkControlName: 'currentMilkProduction' | 'targetMilkProduction',
    fatControlName: 'currentFatPercentage' | 'targetFatPercentage',
    proteinControlName: 'currentProteinPercentage' | 'targetProteinPercentage',
  ): number | null {
    const values = this.productionValues(milkControlName, fatControlName, proteinControlName);
    if (!values) {
      return null;
    }

    return this.dairyYieldCalculationService.calculateFatAndProtein(values);
  }

  private productionValues(
    milkControlName: 'currentMilkProduction' | 'targetMilkProduction',
    fatControlName: 'currentFatPercentage' | 'targetFatPercentage',
    proteinControlName: 'currentProteinPercentage' | 'targetProteinPercentage',
  ): DairyYieldCalculationInput | null {
    const milkProduction = this.contextForm.controls[milkControlName];
    const fatPercentage = this.contextForm.controls[fatControlName];
    const proteinPercentage = this.contextForm.controls[proteinControlName];

    if (
      milkProduction.invalid ||
      fatPercentage.invalid ||
      proteinPercentage.invalid ||
      milkProduction.value === null ||
      fatPercentage.value === null ||
      proteinPercentage.value === null
    ) {
      return null;
    }

    return {
      milkProduction: roundToDecimals(milkProduction.value, 1),
      fatPercentage: roundToDecimals(fatPercentage.value, 2),
      proteinPercentage: roundToDecimals(proteinPercentage.value, 2),
    };
  }

  private productionInputDecimalPlaces(controlName: ProductionInputControlName): number {
    return controlName.includes('Fat') || controlName.includes('Protein') ? 2 : 1;
  }

  private progressSectionIds(): string[] {
    return [
      'scan-details',
      ...(this.questionnaire()?.categories.map((_, index) => `scan-category-${index}`) ?? []),
    ];
  }
}
